import React, { useState, useEffect, useCallback } from "react";
import { AlertTriangle, Search, ShieldCheck } from "lucide-react";
import Badge from "../../../components/common/Badge";
import Skeleton from "../../../components/common/Skeleton";
import FormControl from "../../../components/common/FormControl";
import NoDataFound from "../../../components/common/NoDataFound";
import Pagination from "../../../components/common/Pagination";
import apiClient from "../../../services/apiService";
import type { BadgeVariant } from "../../../components/common/Badge";

interface RiskItem {
  id: number;
  risk_name: string;
  risk_code: string;
  description?: string;
  category_name?: string;
  is_approved: number;
}

const approvalVariant = (is_approved: number): BadgeVariant => {
  if (is_approved === 1)  return "success";
  if (is_approved === -1) return "danger";
  return "warning";
};

const approvalLabel = (is_approved: number): string => {
  if (is_approved === 1)  return "Approved";
  if (is_approved === -1) return "Rejected";
  return "Pending";
};

const CardSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="action-card action-card--skeleton">
        <div className="action-card__skel-header">
          <Skeleton.Box width={34} height={34} radius={8} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <Skeleton.Box width="60%" height={12} />
            <Skeleton.Box width="40%" height={10} />
          </div>
          <Skeleton.Pill width={64} height={20} />
        </div>
        <div className="action-card__skel-meta">
          <Skeleton.Box width={80} height={10} />
          <Skeleton.Box width={120} height={10} />
        </div>
      </div>
    ))}
  </>
);

const VaultRisksBody: React.FC = () => {
  const [risks, setRisks]               = useState<RiskItem[]>([]);
  const [loading, setLoading]           = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage]                 = useState(1);
  const [pageSize, setPageSize]         = useState(10);
  const [searchTerm, setSearchTerm]     = useState("");

  const fetchRisks = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: pageSize };
      if (searchTerm) params.search = searchTerm;
      const res = await apiClient.get("/api/v1/rsk/risks", { params });
      setRisks(res.data.data || []);
      setTotalRecords(res.data.meta?.total ?? 0);
    } catch {
      setRisks([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm]);

  useEffect(() => { fetchRisks(); }, [fetchRisks]);
  useEffect(() => { setPage(1); }, [searchTerm]);

  return (
    <div className="netbody-card flex flex-col h-full">
      <div className="netbody-header">
        <div style={{ marginLeft: "auto" }}>
          <FormControl.Input
            data-testid="INP_vault_risks_search"
            type="text"
            placeholder="Search risks…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
          />
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container">
          {loading ? (
            <div className="action-card-grid">
              <CardSkeleton count={6} />
            </div>
          ) : risks.length === 0 ? (
            <NoDataFound
              title={searchTerm ? "No Matching Risks" : "No Risks Found"}
              message={
                searchTerm
                  ? "No risks match your search."
                  : "No risks have been added yet."
              }
            />
          ) : (
            <div className="action-card-grid">
              {risks.map((risk) => (
                <div key={risk.id} className="action-card">
                  <div className="action-card__header">
                    <div className="action-card__icon">
                      <AlertTriangle size={16} />
                    </div>
                    <div className="action-card__title-block">
                      <span className="action-card__title">{risk.risk_name}</span>
                      <span className="action-card__subtitle">{risk.risk_code}</span>
                    </div>
                    <div className="action-card__badge">
                      <Badge variant={approvalVariant(risk.is_approved)} size="sm">
                        {approvalLabel(risk.is_approved)}
                      </Badge>
                    </div>
                  </div>

                  {(risk.category_name || risk.description) && (
                    <div className="action-card__meta">
                      {risk.category_name && (
                        <span className="action-card__meta-item">
                          <ShieldCheck size={12} />
                          {risk.category_name}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            recordLabel="risks"
          />
        </div>
      </div>
    </div>
  );
};

export default VaultRisksBody;
